/**
 * Breadcrumbs for dialer satellite pages (version graph, scenarios, compare,
 * simulator) that still live under `/outils/campagnes/workflows/...`.
 *
 * The canonical CRM shell is SFLike (`/dialer-campaigns`, `/dialer-workflows/:id`).
 * These crumbs point there so an operator never lands back on a redirect disguised
 * as a breadcrumb. The pages themselves stay under `/outils/...` because they are
 * legitimate bespoke surfaces (graph / simulator), not record forms.
 *
 * The canvas editor is also keyed by id (`/dialer-workflows/:id/edition`); the helpers
 * below keep every « Edit on canvas » / leave-editor link on that form.
 */

export type DialerCrumb = { name: string; href?: string };

/** SFLike Informations tab of one workflow branch. */
export function workflowInformationPath(workflowId: number | null | undefined): string {
    return workflowId != null ? `/dialer-workflows/${workflowId}/informations` : "/dialer-workflows";
}

/** Full-window canvas editor for one workflow, keyed by primary key. */
export function workflowEditorPath(workflowId: number, query?: { from_version?: number | string }): string {
    const base = `/dialer-workflows/${workflowId}/edition`;
    if (query?.from_version != null) return `${base}?from_version=${query.from_version}`;
    return base;
}

/** Published graph screen reached after a successful publication. */
export function workflowVersionPath(workflowCode: string, version: number | string): string {
    return `/outils/campagnes/workflows/${workflowCode}/v/${version}`;
}

/** Dialer campaigns → Workflows → branch → optional trailing crumbs. */
export function workflowSatelliteCrumbs(args: {
    workflowId: number | null | undefined;
    workflowName: string;
    /** Extra crumbs after the branch (version, scenarios, …). */
    rest?: DialerCrumb[];
}): DialerCrumb[] {
    return [
        { name: "Dialer campaigns", href: "/dialer-campaigns" },
        { name: "Workflows", href: "/dialer-workflows" },
        { name: args.workflowName, href: workflowInformationPath(args.workflowId) },
        ...(args.rest ?? [])
    ];
}
